import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { navItemForPath } from "@/lib/apps";
import { makeVisible, quickItems } from "@/lib/navModel";

// Full-screen flows that have their own bottom action bar: the tab bar steps aside there.
const OWN_BOTTOM_BAR = ["/quotes/walk", "/quotes/builder", "/virtual-catalogue"];
export const showsTabBar = (pathname) => !OWN_BOTTOM_BAR.some((p) => pathname.startsWith(p));

/** Phones and portrait tablets: the person's four most-used pages one tap away, and Menu for the rest.
 * Which four depends on what their Role opens (navModel QUICK_IDS): sales get Home, Leads, Store walk, Quotes;
 * accounts get Home, Outstanding, Invoices, Payments. */
export default function MobileTabBar({ onMenu }) {
  const { pathname } = useLocation();
  const { user, canAccess, canSeeCost } = useAuth();
  const items = useMemo(() => quickItems(makeVisible({ user, canAccess, canSeeCost })), [user, canAccess, canSeeCost]);
  const current = navItemForPath(pathname);
  if (!showsTabBar(pathname)) return null;
  const cell = "touch-target flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-1.5 text-[11px] font-medium rounded-[var(--radius-md)] focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]";
  return (
    <nav aria-label="Quick menu" data-testid="mobile-tabbar"
         className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-[var(--color-surface)] border-t border-[var(--color-border)] shadow-[0_-2px_10px_rgba(0,0,0,0.05)]"
         style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)", paddingLeft: "env(safe-area-inset-left, 0px)", paddingRight: "env(safe-area-inset-right, 0px)" }}>
      <div className="max-w-xl mx-auto flex items-stretch gap-1 px-2 h-16">
        {items.map((item) => {
          const Icon = item.icon || LayoutDashboard;
          const active = current?.id === item.id;
          return (
            <Link key={item.id} to={item.to} aria-current={active ? "page" : undefined} data-testid={`tab-${item.id}`}
                  className={`${cell} ${active ? "text-[var(--color-primary-hover)]" : "text-[var(--color-text-muted)] hover:text-[var(--color-text)]"}`}>
              <span className={`grid place-items-center w-11 h-7 rounded-full ${active ? "bg-[var(--color-primary-soft)]" : ""}`}>
                <Icon size={19} strokeWidth={active ? 2.25 : 1.75} aria-hidden="true" />
              </span>
              <span className="truncate max-w-full">{item.short}</span>
            </Link>
          );
        })}
        <button type="button" onClick={onMenu} data-testid="tab-menu"
                className={`${cell} text-[var(--color-text-muted)] hover:text-[var(--color-text)]`}>
          <span className="grid place-items-center w-11 h-7"><Menu size={19} aria-hidden="true" /></span>
          <span>Menu</span>
        </button>
      </div>
    </nav>
  );
}
